import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSupabaseBroadcast } from "@/hooks/use-supabase-broadcast";
import {
      type SearchActivityRealtimePayload,
      type SearchExecutionActivity,
      searchActivityFromRealtimePayload,
} from "@/lib/search-activity";
import { useTRPC } from "@/lib/trpc";

function upsertPaginatedSearchActivity<TItem extends SearchExecutionActivity, TResult extends { items: Array<TItem> }>(
      previous: TResult | undefined,
      activity: SearchExecutionActivity,
): TResult | undefined {
      if (!previous) {
            return previous;
      }

      const existingIndex = previous.items.findIndex((item) => item.id === activity.id);
      const nextItems = [...previous.items];

      if (existingIndex >= 0) {
            nextItems[existingIndex] = {
                  ...nextItems[existingIndex],
                  ...activity,
            };
      } else {
            nextItems.unshift(activity as TItem);
      }

      return {
            ...previous,
            items: nextItems,
      };
}

export function useSearchSpecificationActivity(
      searchSpecificationId: string | null | undefined,
      options?: { enabled?: boolean },
) {
      const trpc = useTRPC();
      const queryClient = useQueryClient();
      const noBatchRequest = { trpc: { context: { noBatch: true } } } as const;
      const [latestEvent, setLatestEvent] = useState<SearchExecutionActivity | null>(null);
      const enabled = Boolean(searchSpecificationId) && (options?.enabled ?? true);

      const activityQuery = useQuery(
            trpc.searchSpecifications.getSearchSpecificationActivity.queryOptions(
                  { id: searchSpecificationId ?? "" },
                  {
                        enabled,
                        ...noBatchRequest,
                  },
            ),
      );

      const setActivityCache = (activity: SearchExecutionActivity | null) => {
            if (!searchSpecificationId) {
                  return;
            }

            queryClient.setQueryData(
                  trpc.searchSpecifications.getSearchSpecificationActivity.queryKey({
                        id: searchSpecificationId,
                  }),
                  activity,
            );
            queryClient.setQueryData(
                  trpc.searchExecutions.getLatestSearchSpecificationSearchExecution.queryKey({
                        searchSpecificationId,
                  }),
                  activity,
            );
            queryClient.setQueriesData(
                  {
                        queryKey: trpc.searchExecutions.getSearchExecutions.queryKey({
                              searchSpecificationId,
                        }),
                  },
                  (previous) => {
                        if (!activity) {
                              return previous;
                        }

                        return upsertPaginatedSearchActivity(
                              previous as { items: Array<SearchExecutionActivity> } | undefined,
                              activity,
                        );
                  },
            );

            if (activity) {
                  queryClient.setQueriesData(
                        {
                              queryKey: trpc.searchExecutions.getSearchExecutionsByBrandId.queryKey({
                                    brandId: activity.brandId,
                              }),
                        },
                        (previous) =>
                              upsertPaginatedSearchActivity(
                                    previous as { items: Array<SearchExecutionActivity> } | undefined,
                                    activity,
                              ),
                  );
            }
      };

      useSupabaseBroadcast({
            channelName: searchSpecificationId ? `search_specification:${searchSpecificationId}` : "",
            event: "search_activity_updated",
            callbackFn: (message: { payload?: SearchActivityRealtimePayload }) => {
                  if (!message.payload) {
                        return;
                  }

                  const activity = searchActivityFromRealtimePayload(message.payload);
                  setLatestEvent(activity);
                  setActivityCache(activity);
            },
      });

      return {
            activity: latestEvent ?? activityQuery.data ?? null,
            latestEvent,
            query: activityQuery,
            setOptimisticActivity: (activity: SearchExecutionActivity | null) => {
                  setLatestEvent(activity);
                  setActivityCache(activity);
            },
      };
}
